import type {
  DynamicQuestionAnswerValue,
  DynamicQuestionData,
} from 'src/components/molecules/DynamicQuestion/types';
import { parseUploadedFilesFromAnswerValue } from 'src/utils/dynamicQuestionUploadedFiles';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Maps persisted questionnaire answer JSON (GraphQL `JSON` / disclosure value)
 * into the shape expected by `DynamicQuestion` / `DynamicQuestionReadOnlyValue`.
 */
export function normalizeDynamicQuestionAnswerValue(
  value: unknown,
): DynamicQuestionAnswerValue {
  if (value == null) {
    return {};
  }

  const parsedUploadedFiles = parseUploadedFilesFromAnswerValue(value);

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return {
      text: String(value),
      ...(parsedUploadedFiles.length > 0 && {
        uploadedFiles: parsedUploadedFiles,
        fileNames: parsedUploadedFiles.map((f) => f.fileName),
      }),
    };
  }
  if (!isPlainObject(value)) {
    return {
      ...(parsedUploadedFiles.length > 0 && {
        uploadedFiles: parsedUploadedFiles,
        fileNames: parsedUploadedFiles.map((f) => f.fileName),
      }),
    };
  }
  const answerValue = value as DynamicQuestionAnswerValue;
  const explicitUploaded = answerValue.uploadedFiles;
  const mergedUploadedFiles =
    explicitUploaded != null && explicitUploaded.length > 0
      ? explicitUploaded
      : parsedUploadedFiles;
  const mergedFileNames =
    answerValue.fileNames != null && answerValue.fileNames.length > 0
      ? answerValue.fileNames
      : mergedUploadedFiles.length > 0
        ? mergedUploadedFiles.map((f) => f.fileName)
        : undefined;
  return {
    text: answerValue.text,
    selectedOptionId: answerValue.selectedOptionId,
    selectedOptionIds: answerValue.selectedOptionIds,
    textValues:
      answerValue.textValues != null
        ? answerValue.textValues.map((v) => String(v))
        : undefined,
    dataGridRows: answerValue.dataGridRows,
    dateTimeUnsure: answerValue.dateTimeUnsure,
    evidenceUploadFiles: answerValue.evidenceUploadFiles,
    qaAnswerAttachments: answerValue.qaAnswerAttachments,
    isFlagged: answerValue.isFlagged,
    isSanitized: answerValue.isSanitized,
    ...(mergedUploadedFiles.length > 0 && {
      uploadedFiles: mergedUploadedFiles,
      fileNames: mergedFileNames ?? mergedUploadedFiles.map((f) => f.fileName),
    }),
  };
}

/**
 * Indexes questionnaire questions by id for read-only dynamic question rendering.
 */
export function buildDynamicQuestionsByQuestionId(
  questions: ReadonlyArray<{ id?: string | null }> | null | undefined,
): Record<string, DynamicQuestionData> {
  if (questions == null || questions.length === 0) {
    return {};
  }
  const map: Record<string, DynamicQuestionData> = {};
  for (const q of questions) {
    const id = q.id ?? '';
    if (id === '') continue;
    map[id] = q as DynamicQuestionData;
  }
  return map;
}

export type ReadOnlyQuestionAnswerRow = {
  question: DynamicQuestionData;
  answerValue: unknown;
};

type QuestionnaireQuestionForMerge = DynamicQuestionData & {
  orderIndex?: number | null;
};

type DisclosureAnswerForMerge = {
  questionId?: string | null;
  questionText?: string | null;
  value?: unknown;
};

/**
 * One row per questionnaire question (ordered by `orderIndex`), joined with saved
 * answers so unanswered questions still render with empty values (read-only shows `—`).
 * When questionnaire questions are unavailable, falls back to one row per returned answer.
 */
export function mergeQuestionnaireRowsForReadOnlyDisplay(
  questions: ReadonlyArray<QuestionnaireQuestionForMerge> | null | undefined,
  answers: ReadonlyArray<DisclosureAnswerForMerge> | null | undefined,
): ReadOnlyQuestionAnswerRow[] {
  const answersList = answers ?? [];
  const answerByQuestionId = new Map<string, DisclosureAnswerForMerge>();
  for (const a of answersList) {
    const qid = a.questionId != null ? String(a.questionId) : '';
    if (qid !== '') {
      answerByQuestionId.set(qid, a);
    }
  }

  if (questions != null && questions.length > 0) {
    const withIds = questions.filter(
      (q) => q.id != null && String(q.id) !== '',
    );
    const sorted = [...withIds].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
    );
    const usedIds = new Set<string>();
    const rows: ReadOnlyQuestionAnswerRow[] = sorted.map((q) => {
      const id = String(q.id);
      usedIds.add(id);
      const match = answerByQuestionId.get(id);
      return {
        question: q as DynamicQuestionData,
        answerValue: match?.value,
      };
    });
    for (const a of answersList) {
      const qid = a.questionId != null ? String(a.questionId) : '';
      if (qid === '') {
        rows.push({
          question: {
            id: a.questionId,
            questionText: a.questionText ?? '',
          } as DynamicQuestionData,
          answerValue: a.value,
        });
        continue;
      }
      if (!usedIds.has(qid)) {
        usedIds.add(qid);
        rows.push({
          question: {
            id: a.questionId,
            questionText: a.questionText ?? '',
          } as DynamicQuestionData,
          answerValue: a.value,
        });
      }
    }
    return rows;
  }

  return answersList.map((a) => ({
    question: {
      id: a.questionId,
      questionText: a.questionText ?? '',
    } as DynamicQuestionData,
    answerValue: a.value,
  }));
}
